import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, ChevronRight, Newspaper } from 'lucide-react';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { GameCard } from '@/components/common/GameCard';
import { Badge } from '@/components/ui/badge';

export const LeaguePage = ({ leagues }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [engagementScores, setEngagementScores] = useState({});
  
  const league = leagues?.find(l => l.slug === slug);
  
  useEffect(() => {
    const loadData = async () => {
      if (!league) return;
      try {
        const [gamesRes, newsRes] = await Promise.all([
          api.getGames({ league_id: league.id }),
          api.getNews({ league_id: league.id, limit: 5 })
        ]);
        setGames(gamesRes.data);
        setNews(newsRes.data);
        try { const e = await api.getEngagementScores(); setEngagementScores(e.data.scores || {}); } catch {}
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [league]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  if (!league) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">League not found</p>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        {league.logo && (
          <img src={resolveMediaUrl(league.logo)} alt={league.name} className="h-16 w-16 object-contain" />
        )}
        <div>
          <h1 className="text-3xl font-bold text-white">{league.name}</h1>
          <p className="text-gray-400">Active Competitions & Fixtures</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Games Section - Main Column */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold text-white mb-4">Matches</h2>
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={`game-skeleton-${i}`} className="bg-gray-800/50 rounded-lg p-4 animate-pulse">
                  <div className="h-20 bg-gray-700 rounded" />
                </div>
              ))}
            </div>
          ) : games.length === 0 ? (
            <div className="bg-gray-800/50 rounded-lg p-8 text-center border border-gray-700">
              <p className="text-gray-400">No games scheduled for this league.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {games.map(game => (
                <GameCard key={game.id} game={game} onWatch={() => navigate(`/watch/${game.id}`)} engagementScore={engagementScores[game.id]?.score || 0} />
              ))}
            </div>
          )}
        </div>

        {/* News Section - Sidebar */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-sky-400" />
              League News
            </h2>
            <Link to="/news" className="text-sky-400 hover:text-sky-300 text-sm">
              View All
            </Link>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={`game-skeleton-${i}`} className="bg-gray-800/50 rounded-lg p-4 animate-pulse">
                  <div className="h-4 bg-gray-700 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-700 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : news.length === 0 ? (
            <div className="bg-gray-800/50 rounded-lg p-6 text-center border border-gray-700">
              <Newspaper className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No news for this league yet.</p>
              <Link to="/news" className="text-sky-400 hover:text-sky-300 text-sm mt-2 inline-block">
                Browse all news
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {news.map((article, index) => (
                <Link
                  key={article.id}
                  to={`/news/${article.slug}`}
                  className="group block bg-gray-800/50 rounded-lg overflow-hidden border border-gray-700 hover:border-sky-500/50 transition-all"
                >
                  {/* Show image only for first article */}
                  {index === 0 && (
                    <div className="h-32 relative overflow-hidden">
                      {article.featured_image ? (
                        <img 
                          src={resolveMediaUrl(article.featured_image)} 
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-sky-900 to-blue-900" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="text-white font-medium group-hover:text-sky-400 transition-colors line-clamp-2 text-sm">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <Calendar className="w-3 h-3" />
                      {formatDate(article.published_at || article.created_at)}
                      <ChevronRight className="w-3 h-3 ml-auto text-sky-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaguePage;
